import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import classNames from 'classnames';

import styles from './ImageMarquee.module.css';

import type { PictureSources } from '@farmersdog/corgi-x';
import { Picture, Text } from '@farmersdog/corgi-x';

interface MarqueeItemProps {
  index: number;
  name: string;
  state: string;
  sources: PictureSources[];
}

interface ImageMarqueeProps {
  direction: 'left' | 'right' | 'up' | 'down';
  speed?: number; // Pixels per second
  className?: string;
  items: MarqueeItemProps[];
  ariaLabel: string;
}

const MarqueeItem = ({ name, state, sources, index }: MarqueeItemProps) => {
  return (
    <div className={styles.imageContainer} data-index={`${name}-${index}`}>
      <div className={styles.imageTagContainer}>
        <Text className={styles.imageTag} color="white" bold>
          {name}
        </Text>
        <Text className={styles.imageTag} color="white">
          {' | '}
          {state}
        </Text>
      </div>
      <Picture
        sources={sources}
        aria-hidden
        alt=""
        className={styles.image}
        key={`picture-${name}-${index}`}
      />
    </div>
  );
};

export function ImageMarquee({
  direction = 'left',
  speed = 45,
  className,
  items = [],
  ariaLabel,
}: ImageMarqueeProps) {
  const isVertical = direction === 'up' || direction === 'down';
  const [isPaused, setIsPaused] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // Refs for the container and content elements
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // State to track content dimensions
  const [contentSize, setContentSize] = useState(0);

  // Number of backup copies to create above and below
  const surroundingBackup = 2;

  const pauseMarquee = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeMarquee = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isPaused || isResizing) {
      return;
    }

    const scrollElement = scrollRef.current;
    const backupSize = contentSize * surroundingBackup;

    if (isVertical) {
      const scrollTop = scrollElement.scrollTop;
      if (scrollTop < backupSize || scrollTop >= backupSize + contentSize) {
        scrollElement.scrollTop = backupSize + (scrollTop % contentSize);
      }
    } else {
      const scrollLeft = scrollElement.scrollLeft;
      if (scrollLeft < backupSize || scrollLeft >= backupSize + contentSize) {
        scrollElement.scrollLeft = backupSize + (scrollLeft % contentSize);
      }
    }
  }, [contentSize, isPaused, isVertical, surroundingBackup, isResizing]);

  // Auto-scroll animation
  useEffect(() => {
    if (!scrollRef.current || isPaused || isResizing) {
      return;
    }

    const scrollElement = scrollRef.current;
    let lastTimestamp = 0;
    let animationFrameId: number;

    const animate = (timestamp: number) => {
      if (!lastTimestamp) {
        lastTimestamp = timestamp;
      }
      const delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      // Calculate pixels to move based on speed and time delta
      const pixelsToMove = (speed * delta) / 1000;

      if (isVertical) {
        scrollElement.scrollTop +=
          direction === 'up' ? pixelsToMove : -pixelsToMove;
      } else {
        scrollElement.scrollLeft +=
          direction === 'right' ? -pixelsToMove : pixelsToMove;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [direction, isPaused, isVertical, speed, isResizing]);

  // Measure content size and container size, and set initial scroll position
  useLayoutEffect(() => {
    if (!containerRef.current || !contentRef.current || !scrollRef.current) {
      return;
    }

    if (isVertical) {
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = contentRef.current.offsetHeight;

      setContentSize(contentHeight);

      // Set the height of the scroll container based on the parent container
      scrollRef.current.style.height = `${containerHeight}px`;

      // Set initial scroll position based on direction
      if (direction === 'down') {
        // For 'down' direction, start at the beginning of the content
        scrollRef.current.scrollTop = contentHeight * surroundingBackup;
      } else {
        // For 'up' direction, start at the beginning of the content
        scrollRef.current.scrollTop = contentHeight * surroundingBackup;
      }
    } else {
      const contentWidth = contentRef.current.offsetWidth;
      setContentSize(contentWidth);

      // Set initial scroll position based on direction
      if (direction === 'right') {
        // For 'right' direction, start at the beginning of the content
        scrollRef.current.scrollLeft = contentWidth * surroundingBackup;
      } else {
        // For 'left' direction, start at the beginning of the content
        scrollRef.current.scrollLeft = contentWidth * surroundingBackup;
      }
    }
  }, [isVertical, surroundingBackup, direction]);

  // Recalculate sizes on window resize
  const handleResize = useCallback(() => {
    // Set isResizing to true to pause animation
    setIsResizing(true);

    if (!containerRef.current || !contentRef.current || !scrollRef.current) {
      setIsResizing(false);
      return;
    }

    if (isVertical) {
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = contentRef.current.offsetHeight;

      setContentSize(contentHeight);
      scrollRef.current.style.height = `${containerHeight}px`;
    } else {
      const contentWidth = contentRef.current.offsetWidth;
      setContentSize(contentWidth);
    }

    // Use requestAnimationFrame to ensure the resize is complete
    // before resuming animation
    requestAnimationFrame(() => {
      setIsResizing(false);
    });
  }, [isVertical]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Create arrays of backup content
  const backupContent = Array(surroundingBackup).fill(null);

  return (
    <div
      className={classNames(styles.wrapper, className)}
      ref={containerRef}
      onMouseEnter={pauseMarquee}
      onMouseLeave={resumeMarquee}
      onTouchStart={pauseMarquee}
      onTouchEnd={resumeMarquee}
      onFocus={pauseMarquee}
      onBlur={resumeMarquee}
      role="marquee"
      aria-label={ariaLabel}
      aria-live="off"
    >
      <div
        ref={scrollRef}
        className={classNames(styles.marqueeScroll, {
          [styles.vertical]: isVertical,
          [styles.resizing]: isResizing,
        })}
        onScroll={handleScroll}
      >
        {/* Backup content before */}
        {backupContent.map((_, index) => (
          <div
            key={`before-${index}`}
            className={classNames(styles.container, {
              [styles.vertical]: isVertical,
            })}
          >
            {items.map(({ name, state, sources }, itemIndex) => (
              <MarqueeItem
                key={`before-item-${itemIndex}`}
                index={itemIndex}
                name={name}
                state={state}
                sources={sources}
              />
            ))}
          </div>
        ))}

        {/* Main content */}
        <div
          ref={contentRef}
          className={classNames(styles.container, {
            [styles.vertical]: isVertical,
          })}
        >
          {items.map(({ name, state, sources }, index) => (
            <MarqueeItem
              key={`main-item-${index}`}
              index={index}
              name={name}
              state={state}
              sources={sources}
            />
          ))}
        </div>

        {/* Backup content after */}
        {backupContent.map((_, index) => (
          <div
            key={`after-${index}`}
            className={classNames(styles.container, {
              [styles.vertical]: isVertical,
            })}
          >
            {items.map(({ name, state, sources }, itemIndex) => (
              <MarqueeItem
                key={`after-item-${itemIndex}`}
                index={itemIndex}
                name={name}
                state={state}
                sources={sources}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
